/**
 * Where a visitor first came from: the channel (search, AI assistant,
 * referral, email, direct...) of their first visit, kept so a sign-up or a
 * Lite purchase days later can be credited to it.
 *
 * GA4 does this for itself, but only for browsers that load it: an ad
 * blocker hides a buyer from GA4 entirely, and its sources cannot be joined
 * to `billing_events` or an account. This copy rides through checkout as
 * Stripe metadata (`create-checkout` → `billing_events.acq_*`) and is saved
 * with the account at sign-up or purchase (`account_acquisition`), so "which
 * channel sells Lite" is one SQL query.
 *
 * First touch, on purpose: GA4's own session source already gives the last
 * one. A visit that says nothing about its origin (a reload, the return from
 * Google sign-in or Stripe, a link from our own pages) never replaces it.
 *
 * Nothing typed by the visitor is kept: the referrer's host only, the landing
 * page's path only (a query string can carry a typed owner name), and of the
 * campaign tags only `utm_source`/`utm_medium`/`utm_campaign`, which we or a
 * linking site wrote. `utm_term` can be a search phrase, so it is never read.
 */

export type AcquisitionChannel =
  | "organic_search"
  | "ai_assistant"
  | "social"
  | "email"
  | "paid"
  | "campaign"
  | "referral"
  | "direct";

export interface Acquisition {
  channel: AcquisitionChannel;
  /** The search engine, site or `utm_source` (`google`, `reddit.com`, `newsletter`). */
  source: string | null;
  medium: string | null;
  campaign: string | null;
  /** Host only, `www.` dropped. */
  referrer_host: string | null;
  /** Path only, lowercased. */
  landing_path: string;
  /** ISO timestamp of that first visit. */
  first_seen_at: string;
}

export const ACQUISITION_STORAGE_KEY = "wpm_first_touch";

/** Long enough to cover a yearly plan bought on a later visit. */
export const ACQUISITION_TTL_MS = 365 * 24 * 60 * 60 * 1000;

/** Our own hosts (and the apex or `www.` of whichever one this is). */
const OWN_HOSTS = ["wisconsinpropertymap.com"];

/**
 * Hosts a visitor passes through on the way back to us, never ones that sent
 * them: Google sign-in, Supabase's auth links and Stripe Checkout and its
 * portal. GA4 needs the same hosts under "List unwanted referrals".
 */
const PASS_THROUGH_HOSTS = [
  "accounts.google.com",
  "supabase.co",
  "checkout.stripe.com",
  "billing.stripe.com",
  "stripe.com",
];

/** Checked before search engines: `gemini.google.com` is not Google search. */
const AI_ASSISTANTS: Record<string, string> = {
  "chatgpt.com": "chatgpt",
  "chat.openai.com": "chatgpt",
  "openai.com": "chatgpt",
  "claude.ai": "claude",
  "perplexity.ai": "perplexity",
  "gemini.google.com": "gemini",
  "copilot.microsoft.com": "copilot",
  "you.com": "you",
  "phind.com": "phind",
};

/** Matched on the host's registrable label, so every `google.*` country domain counts. */
const SEARCH_ENGINES: Array<[RegExp, string]> = [
  [/(^|\.)google\.[a-z.]+$/, "google"],
  [/(^|\.)bing\.com$/, "bing"],
  [/(^|\.)duckduckgo\.com$/, "duckduckgo"],
  [/(^|\.)search\.yahoo\.com$/, "yahoo"],
  [/(^|\.)yahoo\.com$/, "yahoo"],
  [/(^|\.)ecosia\.org$/, "ecosia"],
  [/(^|\.)search\.brave\.com$/, "brave"],
  [/(^|\.)startpage\.com$/, "startpage"],
  [/(^|\.)yandex\.[a-z.]+$/, "yandex"],
  [/(^|\.)baidu\.com$/, "baidu"],
  [/(^|\.)aol\.com$/, "aol"],
];

const SOCIAL: Array<[RegExp, string]> = [
  [/(^|\.)facebook\.com$|^fb\.me$/, "facebook"],
  [/(^|\.)instagram\.com$/, "instagram"],
  [/^t\.co$|(^|\.)twitter\.com$|^x\.com$/, "x"],
  [/(^|\.)reddit\.com$/, "reddit"],
  [/(^|\.)linkedin\.com$|^lnkd\.in$/, "linkedin"],
  [/(^|\.)pinterest\.[a-z.]+$/, "pinterest"],
  [/(^|\.)youtube\.com$|^youtu\.be$/, "youtube"],
  [/(^|\.)tiktok\.com$/, "tiktok"],
  [/(^|\.)nextdoor\.com$/, "nextdoor"],
  [/(^|\.)threads\.net$/, "threads"],
];

const WEBMAIL: Array<[RegExp, string]> = [
  [/^mail\.google\.com$/, "gmail"],
  [/(^|\.)outlook\.(live|office|office365)\.com$/, "outlook"],
  [/^mail\.yahoo\.com$/, "yahoo_mail"],
  [/^mail\.aol\.com$/, "aol_mail"],
];

/** Ad click ids: their presence means a paid click whatever the referrer says. */
const PAID_CLICK_IDS: Array<[string, string]> = [
  ["gclid", "google"],
  ["gbraid", "google"],
  ["wbraid", "google"],
  ["msclkid", "bing"],
];

const TOKEN = /^[a-z0-9._-]{1,100}$/;
const PATH = /^\/[a-z0-9/._~-]{0,99}$/;

/** A tag or host as stored: lowercase, safe characters only, at most 100. */
export function cleanToken(value: string | null | undefined): string | null {
  if (!value) return null;
  const cleaned = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 100);
  return TOKEN.test(cleaned) ? cleaned : null;
}

/** A landing path as stored: lowercase, trailing slash dropped, unsafe characters replaced. */
export function cleanLandingPath(pathname: string): string {
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const cleaned = (trimmed || "/").toLowerCase().replace(/[^a-z0-9/._~-]+/g, "_").slice(0, 100);
  return PATH.test(cleaned) ? cleaned : "/";
}

function bareHost(host: string): string {
  return host.toLowerCase().replace(/^www\./, "");
}

function isOwnHost(host: string, currentHost: string): boolean {
  const bare = bareHost(host);
  return bare === bareHost(currentHost) || OWN_HOSTS.some((own) => bare === own || bare.endsWith(`.${own}`));
}

function isPassThrough(host: string): boolean {
  const bare = bareHost(host);
  return PASS_THROUGH_HOSTS.some((h) => bare === h || bare.endsWith(`.${h}`));
}

function lookup(table: Array<[RegExp, string]>, host: string): string | null {
  for (const [pattern, name] of table) if (pattern.test(host)) return name;
  return null;
}

function aiAssistant(host: string | null): string | null {
  if (!host) return null;
  for (const [domain, name] of Object.entries(AI_ASSISTANTS)) {
    if (host === domain || host.endsWith(`.${domain}`)) return name;
  }
  return null;
}

/** The channel campaign tags declare. `utm_medium` decides; unknown mediums are a campaign. */
function channelOfMedium(medium: string | null, source: string | null): AcquisitionChannel {
  if (aiAssistant(source)) return "ai_assistant";
  if (!medium) return "campaign";
  if (/^(e-?mail|newsletter)$/.test(medium)) return "email";
  if (/^(cpc|ppc|paid.*|display|cpm|ads?)$/.test(medium)) return "paid";
  if (/^(social|social[-_]media|sm|social[-_]network)$/.test(medium)) return "social";
  if (medium === "organic") return "organic_search";
  if (medium === "referral") return "referral";
  return "campaign";
}

export interface ArrivalInput {
  /** `window.location.href`. */
  href: string;
  /** `document.referrer`. */
  referrer: string;
  now?: number;
}

/**
 * What this page load says about where the visitor came from, or null when it
 * says nothing: our own pages, or the way back from Google sign-in or Stripe.
 */
export function describeArrival({ href, referrer, now = Date.now() }: ArrivalInput): Acquisition | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  let referrerHost: string | null = null;
  if (referrer) {
    try {
      referrerHost = bareHost(new URL(referrer).host) || null;
    } catch {
      referrerHost = null;
    }
  }
  if (referrerHost && (isOwnHost(referrerHost, url.host) || isPassThrough(referrerHost))) return null;

  const params = url.searchParams;
  const base = {
    referrer_host: cleanToken(referrerHost),
    landing_path: cleanLandingPath(url.pathname),
    first_seen_at: new Date(now).toISOString(),
  };

  const utmSource = cleanToken(params.get("utm_source"));
  const utmMedium = cleanToken(params.get("utm_medium"));
  const utmCampaign = cleanToken(params.get("utm_campaign"));
  if (utmSource || utmMedium || utmCampaign) {
    return {
      channel: channelOfMedium(utmMedium, utmSource),
      source: utmSource ?? cleanToken(referrerHost),
      medium: utmMedium,
      campaign: utmCampaign,
      ...base,
    };
  }

  for (const [param, engine] of PAID_CLICK_IDS) {
    if (params.has(param)) return { channel: "paid", source: engine, medium: "cpc", campaign: null, ...base };
  }

  if (!referrerHost) return { channel: "direct", source: null, medium: null, campaign: null, ...base };

  const ai = aiAssistant(referrerHost);
  if (ai) return { channel: "ai_assistant", source: ai, medium: "referral", campaign: null, ...base };

  // Before search: `mail.google.com` is Gmail, not Google search.
  const mail = lookup(WEBMAIL, referrerHost);
  if (mail) return { channel: "email", source: mail, medium: "email", campaign: null, ...base };

  const engine = lookup(SEARCH_ENGINES, referrerHost);
  if (engine) return { channel: "organic_search", source: engine, medium: "organic", campaign: null, ...base };

  const social = lookup(SOCIAL, referrerHost);
  if (social) return { channel: "social", source: social, medium: "social", campaign: null, ...base };

  return { channel: "referral", source: cleanToken(referrerHost), medium: "referral", campaign: null, ...base };
}

const CHANNELS: ReadonlySet<string> = new Set<AcquisitionChannel>([
  "organic_search",
  "ai_assistant",
  "social",
  "email",
  "paid",
  "campaign",
  "referral",
  "direct",
]);

/** A stored or received record, checked field by field; null if it is not one. */
export function parseAcquisition(value: unknown): Acquisition | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (typeof v.channel !== "string" || !CHANNELS.has(v.channel)) return null;
  if (typeof v.landing_path !== "string" || !PATH.test(v.landing_path)) return null;
  if (typeof v.first_seen_at !== "string" || Number.isNaN(Date.parse(v.first_seen_at))) return null;
  const token = (x: unknown) => (typeof x === "string" && TOKEN.test(x) ? x : null);
  return {
    channel: v.channel as AcquisitionChannel,
    source: token(v.source),
    medium: token(v.medium),
    campaign: token(v.campaign),
    referrer_host: token(v.referrer_host),
    landing_path: v.landing_path,
    first_seen_at: new Date(v.first_seen_at).toISOString(),
  };
}

/** This page load's arrival, for a browser whose storage is blocked. */
let thisVisit: Acquisition | null = null;

function storageRefused(): boolean {
  return typeof window !== "undefined" && window.__storageConsent === false;
}

function readStored(now: number): Acquisition | null {
  try {
    const raw = localStorage.getItem(ACQUISITION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = parseAcquisition(JSON.parse(raw));
    if (!parsed || now - Date.parse(parsed.first_seen_at) > ACQUISITION_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Record this page load's arrival if it is the first one with something to
 * say. Called once per full page load, from `initializeAnalyticsSession`.
 */
export function noteArrival(input?: Partial<ArrivalInput>): void {
  if (typeof window === "undefined") return;
  const now = input?.now ?? Date.now();
  const arrival = describeArrival({
    href: input?.href ?? window.location.href,
    referrer: input?.referrer ?? document.referrer,
    now,
  });
  if (!arrival) return;
  if (!thisVisit) thisVisit = arrival;
  if (storageRefused() || readStored(now)) return;
  try {
    localStorage.setItem(ACQUISITION_STORAGE_KEY, JSON.stringify(arrival));
  } catch {
    // Storage blocked: `firstTouch` falls back to this page load's arrival.
  }
}

/** The visitor's first known arrival, or null when nothing is known. */
export function firstTouch(now = Date.now()): Acquisition | null {
  return readStored(now) ?? thisVisit;
}

/** For tests: forget this page load's arrival. */
export function resetArrivalForTests(): void {
  thisVisit = null;
}
